/**
 * OutboundSetupManager (MA-UI-108)
 * 
 * Admin > Outbound Setup for managing outbound routing destinations.
 * Destinations configured here are referenced by funnels for lead routing.
 * 
 * Features:
 * - List all destinations
 * - Create/Edit/Delete destinations
 * - Set default destination
 * - Test delivery functionality
 */

import React, { useState, useEffect, useCallback } from 'react';
import { 
  Send, 
  Plus, 
  Pencil, 
  Trash2, 
  CheckCircle2, 
  XCircle, 
  AlertTriangle,
  RefreshCw,
  Loader2,
  ExternalLink,
  Star,
  Zap,
  MoreVertical,
  Eye,
  EyeOff,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from '@/hooks/use-toast';
import { api } from '@/lib/api';

// =============================================================================
// DESTINATION CARD
// =============================================================================

function DestinationCard({ 
  destination, 
  onEdit, 
  onDelete, 
  onSetDefault,
  onTestDelivery,
  testingId,
}) {
  const isDefault = destination.is_default;
  const isActive = destination.is_active;
  const lastTestStatus = destination.last_test_status;
  
  return (
    <div 
      className={`bg-slate-800/50 rounded-lg p-4 border ${
        isDefault ? 'border-indigo-500/30' : 'border-white/5'
      }`}
      data-testid={`destination-card-${destination.id}`}
    >
      {/* Header Row */}
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2.5">
          <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${
            isActive ? 'bg-emerald-500/15' : 'bg-slate-600/20'
          }`}>
            <Send className={`h-4 w-4 ${isActive ? 'text-emerald-400' : 'text-slate-500'}`} />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="font-medium text-slate-200">{destination.name}</span>
              {isDefault && (
                <Badge className="text-xs bg-indigo-500/20 text-indigo-300 border-indigo-500/30">
                  <Star className="h-2.5 w-2.5 mr-1" />
                  Default
                </Badge>
              )}
              {!isActive && (
                <Badge className="text-xs bg-slate-500/20 text-slate-400">Inactive</Badge>
              )}
            </div>
            {destination.description && (
              <p className="text-xs text-slate-500 mt-0.5">{destination.description}</p>
            )}
          </div>
        </div>
        
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
              <MoreVertical className="h-4 w-4 text-slate-400" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44">
            <DropdownMenuItem onClick={() => onEdit(destination)}>
              <Pencil className="h-3.5 w-3.5 mr-2" />
              Edit
            </DropdownMenuItem>
            {!isDefault && (
              <DropdownMenuItem onClick={() => onSetDefault(destination.id)}>
                <Star className="h-3.5 w-3.5 mr-2" />
                Set as Default
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem 
              onClick={() => onDelete(destination)}
              className="text-red-400 focus:text-red-300"
            >
              <Trash2 className="h-3.5 w-3.5 mr-2" />
              Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      
      {/* Webhook URL */}
      <div className="text-xs text-slate-500 mb-3">
        <span className="text-slate-600">Webhook:</span>{' '}
        <code className="text-slate-400 font-mono">{destination.webhook_url_masked}</code>
      </div>
      
      {/* Footer Row */}
      <div className="flex items-center justify-between pt-2 border-t border-white/5">
        <div className="flex items-center gap-2">
          {lastTestStatus === 'success' ? (
            <span className="text-xs text-emerald-400 flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3" />
              Last test passed
            </span>
          ) : lastTestStatus === 'failed' ? (
            <span className="text-xs text-red-400 flex items-center gap-1">
              <XCircle className="h-3 w-3" />
              Last test failed
            </span>
          ) : (
            <span className="text-xs text-slate-500">Not tested yet</span>
          )}
        </div>
        
        <Button
          variant="outline"
          size="sm"
          onClick={() => onTestDelivery(destination.id)}
          disabled={testingId === destination.id}
          className="h-7 text-xs border-indigo-500/30 text-indigo-300 hover:bg-indigo-500/10"
          data-testid={`test-delivery-btn-${destination.id}`}
        >
          {testingId === destination.id ? (
            <Loader2 className="h-3 w-3 animate-spin mr-1" />
          ) : (
            <Zap className="h-3 w-3 mr-1" />
          )}
          Test Delivery
        </Button>
      </div>
    </div>
  );
}

// =============================================================================
// CREATE/EDIT DIALOG
// =============================================================================

function DestinationDialog({ 
  open, 
  onOpenChange, 
  destination, 
  onSave,
  saving,
  tenantId,
}) {
  const isEditing = !!destination?.id;
  const params = tenantId ? { tenant_id: tenantId } : {};
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [webhookUrl, setWebhookUrl] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [isDefault, setIsDefault] = useState(false);
  
  // Show full URL toggle state
  const [showFullUrl, setShowFullUrl] = useState(false);
  const [fullUrl, setFullUrl] = useState(null);
  const [loadingFullUrl, setLoadingFullUrl] = useState(false);
  
  useEffect(() => {
    if (destination) {
      setName(destination.name || '');
      setDescription(destination.description || '');
      setWebhookUrl(destination.webhook_url || destination.webhook_url_masked || '');
      setIsActive(destination.is_active ?? true);
      setIsDefault(destination.is_default ?? false);
      // Reset full URL state when destination changes
      setShowFullUrl(false);
      setFullUrl(null);
    } else {
      setName('');
      setDescription('');
      setWebhookUrl('');
      setIsActive(true);
      setIsDefault(false);
      setShowFullUrl(false);
      setFullUrl(null);
    }
  }, [destination, open]);
  
  // Fetch full URL when toggle is enabled
  const handleToggleFullUrl = async () => {
    if (showFullUrl) {
      // Hide URL
      setShowFullUrl(false);
      return;
    }
    
    if (!destination?.id) {
      // New destination - URL is already visible
      setShowFullUrl(true);
      return;
    }
    
    // Fetch full URL from server
    setLoadingFullUrl(true);
    try {
      const res = await api.get(`/outbound-destinations/${destination.id}/reveal-url`, { params });
      if (res.data?.webhook_url) {
        setFullUrl(res.data.webhook_url);
        setWebhookUrl(res.data.webhook_url);
        setShowFullUrl(true);
      }
    } catch (err) {
      toast({
        title: 'Failed to reveal URL',
        description: err.response?.data?.detail || 'Unable to fetch full URL',
        variant: 'destructive',
      });
    } finally {
      setLoadingFullUrl(false);
    }
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim() || !webhookUrl.trim()) {
      toast({
        title: 'Missing required fields',
        description: 'Name and Webhook URL are required',
        variant: 'destructive',
      });
      return;
    }
    
    onSave({
      name: name.trim(),
      description: description.trim() || null,
      webhook_url: webhookUrl.trim(),
      is_active: isActive,
      is_default: isDefault,
    });
  };
  
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass border-white/10 max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-slate-100">
            {isEditing ? 'Edit Destination' : 'Add Destination'}
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            {isEditing 
              ? 'Update the destination configuration.'
              : 'Create a new outbound destination for lead routing.'}
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="name" className="text-slate-300 text-sm">Name</Label>
            <Input
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., GHL – Main Agency"
              className="bg-slate-800/50 border-slate-700/50 text-slate-100"
              data-testid="destination-name-input"
            />
          </div>
          
          <div className="space-y-1.5">
            <Label htmlFor="description" className="text-slate-300 text-sm">Description (optional)</Label>
            <Input
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g., Primary agency webhook for new leads"
              className="bg-slate-800/50 border-slate-700/50 text-slate-100"
            />
          </div>
          
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <Label htmlFor="webhook_url" className="text-slate-300 text-sm">Webhook URL</Label>
              {isEditing && destination?.id && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={handleToggleFullUrl}
                  disabled={loadingFullUrl}
                  className="h-6 px-2 text-xs text-slate-400 hover:text-slate-200"
                  data-testid="toggle-full-url"
                >
                  {loadingFullUrl ? (
                    <Loader2 className="h-3 w-3 animate-spin mr-1" />
                  ) : showFullUrl ? (
                    <EyeOff className="h-3 w-3 mr-1" />
                  ) : (
                    <Eye className="h-3 w-3 mr-1" />
                  )}
                  {showFullUrl ? 'Hide full URL' : 'Show full URL'}
                </Button>
              )}
            </div>
            <Input
              id="webhook_url"
              type="url"
              value={webhookUrl}
              onChange={(e) => setWebhookUrl(e.target.value)}
              placeholder="https://services.leadconnectorhq.com/hooks/..."
              className="bg-slate-800/50 border-slate-700/50 text-slate-100 font-mono text-sm"
              data-testid="destination-webhook-input"
            />
            <p className="text-xs text-slate-500">
              {isEditing && !showFullUrl && destination?.id ? (
                <span className="text-amber-400/70">
                  URL is masked for security. Click "Show full URL" to verify.
                </span>
              ) : (
                'The URL where eligible leads will be delivered'
              )}
            </p>
          </div>
          
          <div className="flex items-center justify-between py-2">
            <div>
              <Label htmlFor="is_active" className="text-slate-300 text-sm">Active</Label>
              <p className="text-xs text-slate-500">Inactive destinations will not receive leads</p>
            </div>
            <Switch
              id="is_active"
              checked={isActive}
              onCheckedChange={setIsActive}
            />
          </div>
          
          <div className="flex items-center justify-between py-2 border-t border-white/5 pt-4">
            <div>
              <Label htmlFor="is_default" className="text-slate-300 text-sm">Set as Default</Label>
              <p className="text-xs text-slate-500">Used when funnels don't specify a destination</p>
            </div>
            <Switch
              id="is_default"
              checked={isDefault}
              onCheckedChange={setIsDefault}
            />
          </div>
          
          <DialogFooter className="pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              className="bg-white/5 text-slate-300 hover:bg-white/10"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving || !name.trim() || !webhookUrl.trim()}
              data-testid="save-destination-btn"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin mr-1" />
              ) : null}
              {isEditing ? 'Save Changes' : 'Create Destination'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export default function OutboundSetupManager({ tenantId }) {
  const [destinations, setDestinations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingDestination, setEditingDestination] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingDestination, setDeletingDestination] = useState(null);
  const [deleting, setDeleting] = useState(false);
  
  // Test delivery state
  const [testingId, setTestingId] = useState(null);
  
  // =============================================================================
  // DATA FETCHING
  // =============================================================================
  
  const fetchDestinations = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = tenantId ? { tenant_id: tenantId } : {};
      const res = await api.get('/outbound-destinations', { params: { ...params, include_inactive: true } });
      setDestinations(res.data.destinations || []);
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Failed to load destinations';
      setError(detail);
    } finally {
      setLoading(false);
    }
  }, [tenantId]);
  
  useEffect(() => {
    fetchDestinations();
  }, [fetchDestinations]);
  
  // =============================================================================
  // HANDLERS
  // =============================================================================
  
  const handleCreate = () => {
    setEditingDestination(null);
    setDialogOpen(true);
  };
  
  const handleEdit = (dest) => {
    setEditingDestination(dest);
    setDialogOpen(true);
  };
  
  const handleSave = async (data) => {
    setSaving(true);
    try {
      const params = tenantId ? { tenant_id: tenantId } : {};
      
      if (editingDestination?.id) {
        await api.put(`/outbound-destinations/${editingDestination.id}`, data, { params });
        toast({ title: 'Destination updated' });
      } else {
        await api.post('/outbound-destinations', data, { params });
        toast({ title: 'Destination created' });
      }
      
      setDialogOpen(false);
      await fetchDestinations();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Failed to save destination';
      toast({ title: 'Save failed', description: detail, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };
  
  const handleDelete = async () => {
    if (!deletingDestination) return;
    
    setDeleting(true);
    try {
      const params = tenantId ? { tenant_id: tenantId } : {};
      await api.delete(`/outbound-destinations/${deletingDestination.id}`, { params });
      toast({ title: 'Destination deleted' });
      setDeleteDialogOpen(false);
      await fetchDestinations();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Failed to delete destination';
      toast({ title: 'Delete failed', description: detail, variant: 'destructive' });
    } finally {
      setDeleting(false);
    }
  };
  
  const handleSetDefault = async (destId) => {
    try {
      const params = tenantId ? { tenant_id: tenantId } : {};
      await api.put(`/outbound-destinations/${destId}`, { is_default: true }, { params });
      toast({ title: 'Default destination updated' });
      await fetchDestinations();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Failed to set default';
      toast({ title: 'Update failed', description: detail, variant: 'destructive' });
    }
  };
  
  const handleTestDelivery = async (destId) => {
    setTestingId(destId);
    try {
      const params = tenantId ? { tenant_id: tenantId } : {};
      const res = await api.post(`/outbound-destinations/${destId}/test`, null, { params });
      
      if (res.data.success) {
        toast({
          title: 'Test delivery successful',
          description: `Response time: ${res.data.response_time_ms}ms`,
        });
      } else {
        toast({
          title: 'Test delivery failed',
          description: res.data.error || 'Unknown error',
          variant: 'destructive',
        });
      }
      
      await fetchDestinations();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Test delivery failed';
      toast({ title: 'Test failed', description: detail, variant: 'destructive' });
    } finally {
      setTestingId(null);
    }
  };
  
  // =============================================================================
  // RENDER
  // =============================================================================
  
  if (loading) {
    return (
      <Card className="glass border-white/10">
        <CardContent className="py-8 flex items-center justify-center">
          <RefreshCw className="h-5 w-5 animate-spin text-indigo-400" />
          <span className="ml-2 text-slate-400">Loading destinations...</span>
        </CardContent>
      </Card>
    );
  }
  
  if (error) {
    return (
      <Card className="glass border-white/10">
        <CardContent className="p-4">
          <div className="flex items-center gap-3 text-red-300">
            <AlertTriangle className="h-4 w-4 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-medium">Failed to load destinations</p>
              <p className="text-xs text-red-400/80">{error}</p>
            </div>
            <Button variant="outline" size="sm" onClick={fetchDestinations}>
              <RefreshCw className="h-3 w-3 mr-1" />
              Retry
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <div data-testid="outbound-setup-manager">
      <Card className="glass border-white/10">
        {/* Card Header */}
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl text-white flex items-center gap-2">
                <Send className="h-5 w-5 text-indigo-400" />
                Outbound Setup
              </CardTitle>
              <CardDescription className="text-slate-400 mt-1">
                Configure destinations for lead routing to external systems
              </CardDescription>
            </div>
            <Button onClick={handleCreate} size="sm" data-testid="add-destination-btn">
              <Plus className="h-4 w-4 mr-1" />
              Add Destination
            </Button>
          </div>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {destinations.length === 0 ? (
            /* Empty State */
            <div className="text-center py-8 px-4 bg-slate-800/30 rounded-lg border border-dashed border-white/10">
              <Send className="h-10 w-10 text-slate-600 mx-auto mb-3" />
              <h3 className="text-lg font-medium text-slate-300 mb-2">No Destinations Configured</h3>
              <p className="text-slate-500 text-sm mb-4 max-w-md mx-auto">
                Create an outbound destination to start routing eligible leads to your CRM or external systems.
              </p>
              <Button onClick={handleCreate} data-testid="empty-add-destination-btn">
                <Plus className="h-4 w-4 mr-1" />
                Add Your First Destination
              </Button>
            </div>
          ) : (
            /* Destination List */
            <div className="space-y-3">
              {destinations.map((dest) => (
                <DestinationCard
                  key={dest.id}
                  destination={dest}
                  onEdit={handleEdit}
                  onDelete={(d) => {
                    setDeletingDestination(d);
                    setDeleteDialogOpen(true);
                  }}
                  onSetDefault={handleSetDefault}
                  onTestDelivery={handleTestDelivery}
                  testingId={testingId}
                />
              ))}
            </div>
          )}
          
          {/* Info Footer */}
          {destinations.length > 0 && (
            <div className="pt-3 border-t border-white/5">
              <p className="text-xs text-slate-500">
                Leads from funnels will be delivered to the resolved destination after eligibility is confirmed.
                Funnels can use the default destination or specify a specific one.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
      
      {/* Create/Edit Dialog */}
      <DestinationDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        destination={editingDestination}
        onSave={handleSave}
        saving={saving}
        tenantId={tenantId}
      />
      
      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="glass border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-slate-100 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-400" />
              Delete Destination
            </AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400 text-sm">
              Are you sure you want to delete "{deletingDestination?.name}"?
              Funnels referencing this destination will fall back to the default.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/5 text-slate-300 hover:bg-white/10 border-white/10">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-500/90 text-white hover:bg-red-500"
            >
              {deleting ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Trash2 className="h-3 w-3 mr-1" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
